<!--
  聊天主界面组件。
  负责消息展示、输入发送、附件上传、模型选择、反馈操作和思考过程抽屉。
-->
<template>
  <div class="chat-wrapper">
    <!-- 头部信息栏 -->
    <header class="chat-header">
      <div class="tags">
        <span class="tag" title="用户ID">用户ID: {{ displayCurrentUserId }}</span>
        <span class="tag" title="会话 ID">会话ID: {{ sessionId || '新会话' }}</span>
        <span class="tag" title="消息数量">msg总数: {{ messages.length }}</span>
      </div>
      <div v-if="shouldShowAnonymousLimitTip" class="anonymous-limit-tip">
        未登录体验：同 IP 每小时可免费聊天 {{ anonymousChatHourlyLimit }} 次，超限后请登录继续使用。
      </div>
    </header>

    <!-- 聊天区域 -->
    <main class="chat-box" ref="chatBoxRef" @scroll="handleChatScroll">
      <div v-if="shouldShowMessageSkeleton" class="message-skeleton-list" aria-label="加载历史消息">
        <div v-for="item in messageSkeletonItems" :key="item.id" class="message-skeleton" :class="item.role">
          <div class="skeleton-bubble">
            <div v-for="line in item.lines" :key="line" class="skeleton-line" :class="`line-${line}`"></div>
          </div>
        </div>
      </div>

      <div v-for="(msg, index) in messages" :key="msg.id" :ref="(element) => setMessageElement(element, index)"
        :class="['msg', getRoleClass(msg.role), {
          'edit-mode-full': msg.role === ChatRole.USER && editingMessageIndex === index,
          'streaming': isAssistantStreaming(msg, index)
        }]">

        <!-- 消息内容 -->
        <div class="msg-content">
          <AgentTracePanel v-if="msg.role === ChatRole.ASSISTANT && hasExecutionProcess(msg, index)"
            :trace-id="getMessageTraceId(msg, index)" :session-id="sessionId" :user-id="currentUserId"
            :thinking-steps="getMessageThinkingSteps(msg)" />

          <!-- 用户消息编辑模式 -->
          <div v-if="msg.role === ChatRole.USER && editingMessageIndex === index" class="edit-mode">
            <el-input v-model="editingContent" type="textarea" :rows="3" resize="none" placeholder="编辑消息..."
              class="edit-input" />
            <div class="edit-actions">
              <el-button size="small" @click="cancelEditUserMsg">取消</el-button>
              <el-button size="small" type="success" @click="submitEditUserMsg(index)"
                :disabled="!editingContent.trim()">
                重新发送
              </el-button>
            </div>
          </div>

          <!-- 用户消息显示模式 -->
          <div v-else-if="msg.role === ChatRole.USER" class="message-text">{{ msg.content }}
            <!-- 显示附件 -->
            <div v-if="hasAttachments(msg)" class="message-attachments">
              <div v-for="attachment in (msg as any).attachments" :key="attachment.id" class="attachment-item">
                <!-- 图片附件 -->
                <el-image v-if="attachment.type === 'image'" :src="attachment.url" :alt="attachment.name"
                  :preview-src-list="[attachment.url]" fit="cover" class="attachment-image" preview-teleported
                  title="点击查看图片" />
                <!-- 文档附件 - 点击下载 -->
                <!-- <div v-else-if="attachment.type === 'doc'" class="attachment-doc" :title="`点击下载: ${attachment.name}`" @click="downloadAttachment(attachment.url, attachment.name)">
                  <span class="doc-icon">{{ getDocIcon(attachment.mimeType) }}</span>
                  <span class="doc-name">{{ attachment.name }}</span>
                </div> -->
                <!-- 文档附件 - 点击在新标签页预览 -->
                <div v-else-if="attachment.type === 'doc'" class="attachment-doc" title="点击在新标签页阅览"
                  @click="openPdfInNewTab(attachment.url)">
                  <span class="doc-icon">{{ getDocIcon(attachment.mimeType) }}</span>
                  <span class="doc-name">{{ attachment.name }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- AI 回复 -->
          <div v-else-if="msg.role === ChatRole.ASSISTANT" class="message-text markdown-body"
            v-html="getRenderedMarkdown(msg)">
          </div>
          <div v-if="isAssistantStreaming(msg, index)" class="streaming-status" aria-live="polite">
            <span class="streaming-dot"></span>
            <span>正在思考并生成回答</span>
          </div>
        </div>

        <!-- 回复操作栏 -->
        <div v-if="isAssistantMessageComplete(msg, index)" class="message-actions ai-actions">
          <button class="action-btn" @click="copyToClipboard(msg.content)" title="复制">
            <el-icon>
              <CopyDocument />
            </el-icon>
          </button>
          <!-- 点赞按钮 -->
          <button class="action-btn feedback-btn" :class="{ 'feedback-active': (msg as any).feedbackType === 'like' }"
            title="点赞此回答" @click="toggleLike(msg.id, currentUserId, messages)">
            👍
          </button>
          <!-- 点踩按钮 -->
          <button class="action-btn feedback-btn"
            :class="{ 'feedback-active': (msg as any).feedbackType === 'dislike' }" title="点踩此回答"
            @click="toggleDislike(msg.id, currentUserId, messages)">
            👎
          </button>
          <button class="action-btn" title="重新生成" @click="handleRegenerate(index)">🔄</button>
          <span v-if="getMessageDisplayTime(msg)" class="message-time reply-time">
            {{ getMessageDisplayTime(msg) }}
          </span>
        </div>

        <!-- 问题操作栏 -->
        <div v-if="msg.role === ChatRole.USER && editingMessageIndex !== index" class="message-actions user-actions">
          <span v-if="getMessageDisplayTime(msg)" class="message-time question-time">
            {{ getMessageDisplayTime(msg) }}
          </span>
          <button class="action-btn" @click="copyToClipboard(msg.content)" title="复制问题">
            <el-icon>
              <CopyDocument />
            </el-icon>
          </button>
          <button class="action-btn" @click="editUserMsg(index, msg.content)" title="编辑">
            <el-icon>
              <Edit />
            </el-icon>
          </button>
          <el-popconfirm confirm-button-text="确定" cancel-button-text="取消" :icon="InfoFilled" icon-color="#626AEF"
            title="确定删除此问题和对应的回复吗？" @confirm="deleteUserMsg(index)">
            <template #reference>
              <button class="action-btn danger" title="删除">
                <el-icon>
                  <Delete />
                </el-icon>
              </button>
            </template>
          </el-popconfirm>
        </div>
      </div>

      <!-- 错误提示 -->
      <div v-if="shouldShowInlineError" class="error-message">
        ❌ {{ displayErrorMessage }}
      </div>

      <!-- 空状态提示 -->
      <div v-if="isShowWelcome && messages.length === 0 && !isLoading && !isChatContentLoading" class="home-message">
        <div class="title">{{ emptyTitle }}</div>
        <div class="questions">
          <span class="question-item" v-for="(item, index) in defaultQuestion"
            @click="handleDefaultQuestionClick(item)">{{
              item }}</span>
        </div>
      </div>
    </main>

    <!-- 提问定位条 s -->
    <nav v-if="isQuestionJumpNavVisible" class="question-jump-nav" aria-label="用户提问导航">
      <span v-for="placeholder in questionJumpPlaceholders" :key="placeholder.id" class="question-jump-placeholder"
        :style="{ top: `calc(50% + ${placeholder.offset}px)` }">
        <span class="marker-line"></span>
      </span>

      <button v-for="marker in userQuestionMarkers" :key="marker.messageId" type="button" class="question-jump-marker"
        :class="{ active: marker.messageIndex === activeQuestionIndex || marker.messageIndex === selectedQuestionIndex }"
        :style="{ top: `calc(50% + ${marker.offset}px)` }" :title="marker.title"
        @click="scrollToQuestion(marker.messageIndex)">
        <span class="marker-line"></span>
      </button>

    </nav>
    <!-- 提问定位条 e -->

    <!-- 输入区域 -->
    <footer class="input-area">
      <div class="input-wrapper">
        <!-- 附件预览区域 -->
        <div v-if="uploadedFiles.length > 0" class="attachments-preview">
          <div v-for="file in uploadedFiles" :key="file.id" class="preview-item">
            <!-- 图片预览 -->
            <img v-if="file.type === 'image'" :src="file.previewUrl || file.url" :alt="file.name"
              class="preview-image" />
            <!-- 文档预览 -->
            <div v-else-if="file.type === 'doc'" class="preview-doc">
              <span class="doc-icon">{{ getDocIcon(file.mimeType) }}</span>
              <span class="doc-name">{{ file.name }}</span>
            </div>
            <!-- 删除文件按钮 -->
            <button class="remove-btn" @click="removeFile(file.id)" title="删除">
              ✕
            </button>
          </div>
        </div>

        <!-- 输入框和按钮 -->
        <div class="input-container" @drop="handleDrop" @dragover="handleDragOver" @paste="handlePaste">
          <div class="prompt-template-bar" aria-label="Prompt templates">
            <button v-for="template in promptTemplates" :key="template.key" type="button" class="prompt-template-btn"
              :class="{ active: selectedPromptTemplateKey === template.key }" :title="template.description"
              @click="applyPromptTemplate(template)">
              <el-icon>
                <component :is="getPromptTemplateIcon(template.key)" />
              </el-icon>
              <span>{{ template.label }}</span>
            </button>
          </div>

          <el-input v-model="input" :placeholder="inputPlaceholder" @keyup.enter.prevent="handleSendMessage"
            :disabled="isLoading" type="textarea" :rows="3" resize="none" />



          <!-- 工具栏 -->
          <div class="toolbar">
            <div class="toolbar-left">
              <!-- 上传文件按钮 -->
              <label class="upload-btn" :class="{ 'disabled': !canAddMore }" title="上传图片、PDF、文档">
                <input type="file" multiple
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml,application/pdf,text/markdown,text/x-markdown,.md,application/msword,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,application/vnd.ms-excel,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx,application/vnd.ms-powerpoint,.ppt,application/vnd.openxmlformats-officedocument.presentationml.presentation,.pptx"
                  @change="handleFileSelect" :disabled="!canAddMore" style="display: none;" />
                <!-- 回形针 -->
                📎
              </label>
              <span v-if="uploadedFiles.length > 0" class="file-count">
                {{ uploadedFiles.length }}/{{ maxFileCount }}
              </span>

              <!-- 模型选择下拉框 -->
              <el-select v-model="selectedModel" size="small" class="model-select"
                style="margin-left: 12px; width: 150px;">
                <el-option label="自动选择" value="auto" />
                <el-option label="DeepSeek V4 Flash (文本)" :value="AIModel.DEEPSEEK" />
                <el-option label="通义千问 VL (图片)" :value="AIModel.QWEN_VL" />
                <el-option label="通义千问 Long (文档)" :value="AIModel.QWEN_LONG" />
              </el-select>
            </div>

            <div class="toolbar-right">
              <el-button type="primary" :loading="isLoading"
                :disabled="isLoading || (!input.trim() && uploadedFiles.length === 0)" @click="handleSendMessage">
                发送
              </el-button>
              <el-button v-if="isLoading" type="danger" @click="stopGeneration">
                停止
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </footer>

    <!-- 声明 -->
    <p class="ai-disclaimer">{{ aiContentDisclaimer }}</p>

    <!-- 回到顶部 -->
    <el-backtop :right="20" :bottom="80" target=".chat-box" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, nextTick, onMounted, onUnmounted, watch, type ComponentPublicInstance } from 'vue'
import { Collection, CopyDocument, Delete, DocumentCopy, Edit, EditPen, InfoFilled, MagicStick, Notebook, Reading } from '@element-plus/icons-vue'
import { useChatSession } from '~/composables/useChatSession'
import { useChatMessages } from '~/composables/useChatMessages'
import { useFileUpload } from '~/composables/useFileUpload'
import AgentTracePanel from '~/components/AgentTracePanel.vue'
import { renderMarkdown, handleCopyClick, copyToClipboard, getRoleDisplayText, getRoleClass, formatChatMessageTime } from '~/utils/index'
import { normalizeChatErrorMessage } from '~/utils/chat-error'
import { ChatRole, AIModel, CHAT_RATE_LIMIT_CONFIG } from '~/enums'
import { PROMPT_TEMPLATES, type PromptTemplate, type PromptTemplateKey } from '~/utils/prompt-templates'

// Props
interface Props {
  emptyTitle?: string
  inputPlaceholder?: string
  isShowWelcome?: boolean
  isPreparingHistory?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  emptyTitle: '我能为你做些什么呢？',
  inputPlaceholder: '输入消息...（例如：北京今天天气怎么样？）',
  isShowWelcome: false,
  isPreparingHistory: false
})

// Emits
const emit = defineEmits<{
  'message-sent': [sessionId: string]
  'session-persisted': [sessionId: string]
  'scroll-bottom': []
}>()

// ==================== Composables ====================

const { sessionId, setSessionId, initSession } = useChatSession()
const { initialized: authInitialized, isLoggedIn } = useAuthUser()
const runtimeConfig = useRuntimeConfig()

const chatMessages = useChatMessages()
const {
  messages,
  input,
  isLoading,
  isLoadingHistory,
  error,
  currentUserId,
  editingMessageIndex,
  editingContent,
  handleSend,
  stopGeneration,
  handleNewChat,
  handleRegenerate,
  editUserMsg,
  cancelEditUserMsg,
  submitEditUserMsg,
  deleteUserMsg,
  toggleLike,
  toggleDislike,
  removeFeedback,
  loadMessageFeedback,
  DISLIKE_REASONS,
  loadHistoryMessages,
  restoreCurrentSessionMessages,
  setActiveMessagesSession,
  clearAllMessages,
  saveMessagesToStorage,
  setOnMessageFinished,
  setOnSessionPersisted,
  selectedModel,
  selectedPromptTemplateKey,
  latestClientTraceId
} = chatMessages

// 文件上传管理
const {
  uploadedFiles,
  canAddMore,
  handleFileSelect,
  handleDrop,
  handleDragOver,
  handlePaste,
  removeFile,
  clearAllFiles,
  uploadAllFiles,
  getAttachments
} = useFileUpload({
  maxImageSize: 5 * 1024 * 1024, // 5MB
  maxPdfSize: 10 * 1024 * 1024, // 10MB
  maxDocSize: 10 * 1024 * 1024, // 10MB
  maxFileCount: 5
})

const maxFileCount = 5

// 设置默认问题（欢迎区快捷提问）
const defaultQuestion = ref(['重庆今天天气怎么样？', '今天 A 股行情如何？', '最近有什么好看的电影？', '有哪些低成本提升居家幸福感的小技巧？', '用 TypeScript 写一个生成 0-100 随机数的函数'])

// ==================== UI 状态 ====================

const chatBoxRef = ref<HTMLElement | null>(null)
const messageElementMap = new Map<number, HTMLElement>()
const isMounted = ref(false)
const renderedMarkdownCache = ref<Record<string, string>>({})
const activeQuestionIndex = ref<number | null>(null)
const selectedQuestionIndex = ref<number | null>(null)
let markdownRenderTimer: ReturnType<typeof setTimeout> | null = null
let questionMarkerFrame: number | null = null

const displayCurrentUserId = computed(() => (isMounted.value ? currentUserId.value : '-'))
const anonymousChatHourlyLimit = computed(() => {
  const configuredLimit = Number(runtimeConfig.public.anonymousChatHourlyLimit)
  return Number.isInteger(configuredLimit) && configuredLimit > 0
    ? configuredLimit
    : CHAT_RATE_LIMIT_CONFIG.ANONYMOUS_HOURLY_LIMIT
})
const shouldShowAnonymousLimitTip = computed(() => {
  return isMounted.value && authInitialized.value && !isLoggedIn.value
})
const isChatContentLoading = computed(() => props.isPreparingHistory || isLoadingHistory.value)
const shouldShowMessageSkeleton = computed(() => isChatContentLoading.value && messages.value.length === 0)
const displayErrorMessage = computed(() => normalizeChatErrorMessage(error.value))
const shouldShowInlineError = computed(() => {
  if (!error.value) {
    return false
  }

  const lastMessage = messages.value[messages.value.length - 1]
  return !(
    lastMessage?.role === ChatRole.ASSISTANT &&
    lastMessage.content === displayErrorMessage.value
  )
})
const promptTemplates = PROMPT_TEMPLATES
const aiContentDisclaimer = '人工智能生成的内容可能不准确'
const DEFAULT_QUESTION_MARKER_COUNT = 5
const QUESTION_MARKER_GAP = 11
const messageSkeletonItems = [
  { id: 'history-skeleton-1', role: 'user', lines: 2 },
  { id: 'history-skeleton-2', role: 'assistant', lines: 4 },
  { id: 'history-skeleton-3', role: 'user', lines: 1 },
  { id: 'history-skeleton-4', role: 'assistant', lines: 5 },
]

type QuestionJumpMarker = {
  messageId: string
  messageIndex: number
  offset: number
  title: string
}

const getCenteredOffset = (index: number, count: number) => {
  return (index - (count - 1) / 2) * QUESTION_MARKER_GAP
}

/**
 * 用户提问数据集
 */
const userQuestionMarkers = computed(() => {
  const userQuestions = messages.value
    .map((msg: any, messageIndex: number) => ({ msg, messageIndex }))
    .filter(({ msg }) => msg.role === ChatRole.USER)

  return userQuestions.map(({ msg, messageIndex }, questionIndex) => {
    const content = String(msg.content || '').trim()
    return {
      messageId: String(msg.id || `user-${messageIndex}`),
      messageIndex,
      offset: getCenteredOffset(questionIndex, userQuestions.length),
      title: content.length > 40 ? `${content.slice(0, 40)}...` : content || '用户提问',
    }
  }) as QuestionJumpMarker[]
})

/**
 * 问答跳转占位符，用于在用户提问较少时保持导航条居中显示。
 */
const questionJumpPlaceholders = computed(() => {
  return Array.from({ length: DEFAULT_QUESTION_MARKER_COUNT }, (_, index) => ({
    id: `placeholder-${index}`,
    offset: getCenteredOffset(index, DEFAULT_QUESTION_MARKER_COUNT),
  }))
})

const isQuestionJumpNavVisible = computed(() => {
  return messages.value.length > 0
})

// 模板 key 作为持久化字段传给后端，图标只在前端做展示映射，避免 UI 调整影响接口数据。
const promptTemplateIconMap: Record<PromptTemplateKey, any> = {
  translate: Collection,
  summarize: Notebook,
  write_code: MagicStick,
  polish: EditPen,
  explain_code: DocumentCopy,
}

// ==================== 工具函数 ====================

/**
 * 判断消息是否有附件
 * @param msg 
 */
const hasAttachments = (msg: any): boolean => {
  return msg.attachments && Array.isArray(msg.attachments) && msg.attachments.length > 0
}

const getMessageTraceId = (msg: any, index: number): string => {
  if (msg.traceId) {
    return msg.traceId
  }

  const isLatestMessage = index === messages.value.length - 1
  return msg.role === ChatRole.ASSISTANT && isLatestMessage ? latestClientTraceId.value : ''
}

const getMessageThinkingSteps = (msg: any) => {
  return Array.isArray(msg.thinkingSteps) ? msg.thinkingSteps : []
}

const hasExecutionProcess = (msg: any, index: number): boolean => {
  return msg.role === ChatRole.ASSISTANT &&
    (getMessageThinkingSteps(msg).length > 0 || Boolean(getMessageTraceId(msg, index)))
}

const isAssistantStreaming = (msg: any, index: number): boolean => {
  return isLoading.value &&
    msg.role === ChatRole.ASSISTANT &&
    index === messages.value.length - 1
}

const isAssistantMessageComplete = (msg: any, index: number): boolean => {
  return msg.role === ChatRole.ASSISTANT &&
    Boolean(msg.content) &&
    !isAssistantStreaming(msg, index)
}

const getMessageDisplayTime = (msg: any): string => {
  return formatChatMessageTime(msg?.createdAt || msg?.timestamp || null)
}

const getPromptTemplateIcon = (key: PromptTemplateKey) => {
  return promptTemplateIconMap[key] || Reading
}

const applyPromptTemplate = (template: PromptTemplate) => {
  const currentInput = input.value.trim()
  // 保留用户已输入内容，避免切换模板时误清空正在编辑的问题。
  input.value = currentInput ? template.content + currentInput : template.content
  selectedPromptTemplateKey.value = template.key
}

const getMessageCacheKey = (msg: any, index: number): string => {
  // 历史消息有数据库 id，流式临时消息可能没有；降级 key 可避免渲染缓存互相覆盖。
  return String(msg.id || `${msg.role}_${index}`)
}

const getRenderedMarkdown = (msg: any): string => {
  const index = messages.value.indexOf(msg)
  const key = getMessageCacheKey(msg, index)
  return renderedMarkdownCache.value[key] || renderMarkdown(msg.content || '')
}

const renderMarkdownCacheNow = () => {
  const nextCache: Record<string, string> = {}
  messages.value.forEach((msg: any, index: number) => {
    if (msg.role === ChatRole.ASSISTANT) {
      nextCache[getMessageCacheKey(msg, index)] = renderMarkdown(msg.content || '')
    }
  })
  renderedMarkdownCache.value = nextCache
}

const scheduleMarkdownRender = () => {
  // 流式输出会高频触发 deep watch，合并渲染可减少 Markdown 解析阻塞输入。
  if (!process.client || markdownRenderTimer) {
    return
  }

  markdownRenderTimer = setTimeout(() => {
    markdownRenderTimer = null
    const render = renderMarkdownCacheNow

    if (document.visibilityState === 'visible' && window.requestIdleCallback) {
      window.requestIdleCallback(render, { timeout: 250 })
      return
    }

    render()
  }, 120)
}

/**
 * 在新标签页打开 PDF 文件
 * @param url PDF 文件的 URL
 */
const openPdfInNewTab = (url: string) => {
  if (url) {
    window.open(url, '_blank')
  }
}

/**
 * 根据 MIME 类型获取文档图标
 * @param mimeType 文件的 MIME 类型
 */
const getDocIcon = (mimeType: string): string => {
  const iconMap: Record<string, string> = {
    'application/pdf': '📄',
    'text/markdown': '📝',
    'text/x-markdown': '📝',
    'application/msword': '📃',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '📃',
    'application/vnd.ms-excel': '📊',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '📊',
    'application/vnd.ms-powerpoint': '📽️',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': '📽️'
  }
  return iconMap[mimeType] || '📁'
}

/**
 * 下载附件
 * @param url 文件的 URL
 * @param name 文件名
 */
const downloadAttachment = (url: string, name: string) => {
  if (!url) return

  const link = document.createElement('a')
  link.href = url
  link.download = name || 'download'
  link.target = '_blank'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const setMessageElement = (element: Element | ComponentPublicInstance | null, index: number) => {
  if (element instanceof HTMLElement) {
    messageElementMap.set(index, element)
    scheduleQuestionMarkerUpdate()
    return
  }

  messageElementMap.delete(index)
}

const updateQuestionMarkers = () => {
  const chatBox = chatBoxRef.value
  if (!chatBox) {
    return
  }

  let nextActiveIndex: number | null = null
  let closestDistance = Number.POSITIVE_INFINITY
  const viewportAnchor = chatBox.scrollTop + 24

  messages.value.forEach((msg: any, index: number) => {
    if (msg.role !== ChatRole.USER) {
      return
    }

    const element = messageElementMap.get(index)
    if (!element) {
      return
    }

    const messageTop = element.offsetTop
    const distance = Math.abs(messageTop - viewportAnchor)
    if (distance < closestDistance) {
      closestDistance = distance
      nextActiveIndex = index
    }
  })

  activeQuestionIndex.value = nextActiveIndex
}

const scheduleQuestionMarkerUpdate = () => {
  if (!process.client || questionMarkerFrame !== null) {
    return
  }

  questionMarkerFrame = window.requestAnimationFrame(() => {
    questionMarkerFrame = null
    updateQuestionMarkers()
  })
}

const handleChatScroll = () => {
  scheduleQuestionMarkerUpdate()
}

const scrollToQuestion = async (index: number) => {
  selectedQuestionIndex.value = index
  await nextTick()
  const chatBox = chatBoxRef.value
  const target = messageElementMap.get(index)
  if (!chatBox || !target) {
    return
  }

  chatBox.scrollTo({
    top: Math.max(target.offsetTop - 12, 0),
    behavior: 'smooth',
  })
}

/**
 * 处理默认问题点击 - 创建新对话并发送消息
 * @param question 问题文本
 */
const handleDefaultQuestionClick = async (question: string) => {
  console.log('👆 点击默认问题:', question)
  // 如果当前已有消息，先创建新会话
  if (messages.value.length > 0) {
    console.log('🆕 检测到当前有消息，创建新会话')
    await handleNewChat()
    await nextTick()
  }

  input.value = question

  // 然后调用 handleSend（不传参，让它从 input.value 读取）
  await nextTick()
  await handleSendMessage()
}

/**
 * 处理发送按钮点击（无参数版本，用于模板绑定）
 */
const handleSendMessage = async () => {
  // 如果有附件，先上传
  if (uploadedFiles.value.length > 0) {
    const uploadResult = await uploadAllFiles()

    if (!uploadResult.success) {
      console.error('❌ 文件上传失败')
      alert('部分文件上传失败，请重试')
      return
    }
  }

  // 获取附件数据
  const attachments = getAttachments()

  // 调用发送方法（传递附件数据）
  const sent = await handleSend(attachments)

  if (!sent) {
    return
  }

  // 清空已上传的文件
  clearAllFiles()
}

/**
 * 滚动到底部
 */
const scrollToBottom = async () => {
  await nextTick()
  if (chatBoxRef.value) {
    chatBoxRef.value.scrollTop = chatBoxRef.value.scrollHeight
    scheduleQuestionMarkerUpdate()
  }
}

// ==================== 生命周期 ====================

/**
 * 设置消息发送完成回调
 */
setOnMessageFinished(async () => {
  scrollToBottom()
  // saveMessagesToStorage() // 保存消息到 localStorage
  emit('message-sent', sessionId.value)
})

setOnSessionPersisted(() => {
  emit('session-persisted', sessionId.value)
})

const restoreMessagesIfNeeded = async () => {
  renderMarkdownCacheNow()
  if (messages.value.length === 0) {
    await restoreCurrentSessionMessages()
    renderMarkdownCacheNow()
    scrollToBottom()
  }
}

const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    restoreMessagesIfNeeded()
  }
}

watch(() => messages.value.length, () => {
  scrollToBottom()
  scheduleQuestionMarkerUpdate()
  emit('scroll-bottom')
})

watch(
  messages,
  () => {
    renderMarkdownCacheNow()
    scheduleMarkdownRender()
    scheduleQuestionMarkerUpdate()
  },
  { deep: true, immediate: true },
)

onMounted(() => {
  isMounted.value = true
  document.addEventListener('click', handleCopyClick)
  document.addEventListener('visibilitychange', handleVisibilityChange)
  scrollToBottom()
  window.addEventListener('resize', scheduleQuestionMarkerUpdate)
})

onUnmounted(() => {
  document.removeEventListener('click', handleCopyClick)
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('resize', scheduleQuestionMarkerUpdate)
  if (markdownRenderTimer) {
    clearTimeout(markdownRenderTimer)
    markdownRenderTimer = null
  }
  if (questionMarkerFrame !== null) {
    window.cancelAnimationFrame(questionMarkerFrame)
    questionMarkerFrame = null
  }
})

// 暴露给父组件
defineExpose({
  sessionId,
  setSessionId,
  initSession,
  input,
  messages,
  clearAllMessages,
  handleNewChat,
  stopGeneration,
  loadHistoryMessages,
  restoreCurrentSessionMessages,
  setActiveMessagesSession,
  isLoading,
  clearMessages: () => {
    chatMessages.clearLocalMessages()
  }
})

</script>

<style scoped lang="scss">
@use './ChatView.scss';
</style>
